import React, { useState, useMemo, useEffect } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  catchAsync,
  checkResponse,
  removeUnderScoreAndCapitalizeFirstLetter,
} from "../../../../utilities/utilities";
import {
  UPDATE_PROMOCODE,
  ADD_PROMOCODE,
  GET_PROMOCODE_BY_ID,
} from "../../../../services/ApiCalls";
import { discountTypeArr } from "../../../../utilities/const";
import Loader from "../../../../components/Common/Loader";

const getSchema = () =>
  z
    .object({
      promocode: z.string({ required_error: "Code is required" }),
      name: z.string({ required_error: "Coupon name is required" }),
      status: z.boolean(),
      type: z.string({ message: "Discount type is required" }),

      endDate: z.string({ message: "Date expires is required" }),
      startDate: z.string({ message: "Start date is required" }),
      discount: z
        .string()
        .refine((data) => !isNaN(data), { message: "Field should be numeric" })
        .optional(),
      amount: z
        .string()
        .refine((data) => !isNaN(data), { message: "Field should be numeric" })
        .optional(),
    })
    .refine(
      (data) => {
        const startDate = new Date(data.startDate);
        const endDate = new Date(data.endDate);
        return startDate < endDate;
      },
      {
        message: "Start date must be before expiry date",
        path: ["startDate"],
      }
    );

const SendNotification = () => {
  const [promoCodeDetails, setPromocodeDetails] = useState();
  const { id } = useParams();
  const schema = useMemo(() => getSchema(id), [id]);
  const navigate = useNavigate();
  const [loader, setLoader] = useState();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      status: true,
      type: "percent",
    },
    values: {
      promocode: promoCodeDetails?.promocode || "",
      name: promoCodeDetails?.name || "",
      discount: promoCodeDetails?.discount
        ? String(promoCodeDetails?.discount)
        : "",
      amount: promoCodeDetails?.amount ? String(promoCodeDetails?.amount) : "",
      status: promoCodeDetails ? promoCodeDetails?.status === "Active" : true,
      type: promoCodeDetails?.type || "percent",

      startDate: promoCodeDetails?.startDate?.slice(0, 16),
      endDate: promoCodeDetails?.endDate?.slice(0, 16),
    },
  });

  const selectedType = watch("type");

  const submitHandler = catchAsync(async (data) => {
    let body = {
      promocode: data.promocode,
      name: data.name,
      type: data.type,

      startDate: data.startDate,
      endDate: data.endDate,
      status: data.status ? "Active" : "Inactive",
      ...(id && { _id: id }),
    };

    if (data.type === "percent") {
      body.discount = data.discount;
    } else if (data.type === "amount") {
      body.amount = data.amount;
    }

    const res = await (id ? UPDATE_PROMOCODE(body) : ADD_PROMOCODE(body));
    const success = checkResponse({ res, showSuccess: true });

    if (success) {
      navigate("/promocode");
    }
  });

  const getData = catchAsync(async () => {
    setLoader(true);
    await GET_PROMOCODE_BY_ID(id).then((res) =>
      checkResponse({
        res,
        setData: setPromocodeDetails,
        setLoader,
      })
    );
  }, setLoader);

  useEffect(() => {
    if (id) getData();
  }, [id]);

  const handleCancel = () => {
    navigate("/promocode");
  };

  return (
    <>
      {loader && <Loader />}
      <section className="addPromo position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to="/promocode"
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {id ? "Edit Coupon Code" : "Add Coupon Code"}
                </h4>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="5" md="6" className="my-2">
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Coupon name:
                        </label>
                        <input
                          type="text"
                          placeholder="Coupon Name"
                          className="form-control"
                          {...register("name")}
                        />
                        {errors?.name && (
                          <p className="text-danger m-0">
                            {errors?.name?.message}
                          </p>
                        )}
                      </div>

                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Start Date
                        </label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          {...register("startDate")}
                        />
                        {errors?.startDate && (
                          <p className="text-danger m-0">
                            {errors?.startDate?.message}
                          </p>
                        )}
                      </div>
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Coupon Code:
                        </label>
                        <input
                          type="text"
                          placeholder="REWARDS4U"
                          className="form-control"
                          {...register("promocode")}
                        />
                        {errors?.promocode && (
                          <p className="text-danger m-0">
                            {errors?.promocode?.message}
                          </p>
                        )}
                      </div>
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Discount Type
                        </label>

                        <Form.Select
                          className="form-control"
                          {...register("type")}
                        >
                          {discountTypeArr.map((item) => (
                            <option key={item} value={item}>
                              {removeUnderScoreAndCapitalizeFirstLetter(item)}
                            </option>
                          ))}
                        </Form.Select>
                        {errors?.type && (
                          <p className="text-danger m-0">
                            {errors?.type?.message}
                          </p>
                        )}
                      </div>
                    </Col>

                    <Col lg="5" md="6" className="my-2">
                      {selectedType === "percent" && (
                        <div className="py-2">
                          <label className="form-label fw-sbold text-muted ps-2 m-0">
                            Discount (%)
                          </label>
                          <input
                            type="number"
                            placeholder="Discount"
                            className="form-control"
                            {...register("discount")}
                          />
                          {errors?.discount && (
                            <p className="text-danger m-0">
                              {errors?.discount?.message}
                            </p>
                          )}
                        </div>
                      )}

                      {selectedType === "amount" && (
                        <div className="py-2">
                          <label className="form-label fw-sbold text-muted ps-2 m-0">
                            Amount (Flat)
                          </label>
                          <input
                            type="number"
                            placeholder="Amount"
                            className="form-control"
                            {...register("amount")}
                          />
                          {errors?.amount && (
                            <p className="text-danger m-0">
                              {errors?.amount?.message}
                            </p>
                          )}
                        </div>
                      )}

                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          End Date
                        </label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          {...register("endDate")}
                        />
                        {errors?.endDate && (
                          <p className="text-danger m-0">
                            {errors?.endDate?.message}
                          </p>
                        )}
                      </div>
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Set Status of Promotion
                        </label>
                        <Form.Check
                          type="switch"
                          id="custom-switch"
                          {...register("status")}
                        />
                      </div>
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="d-flex align-items-center justify-content-center commonBtn GreyBtn"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="d-flex align-items-center justify-content-center commonBtn "
                        >
                          Submit
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default SendNotification;
